import { Faq, NavItem } from '../types/content'

export const navItems: NavItem[] = [
  { label: 'Programs', to: '/programs' },
  { label: 'Pharmacy', to: '/shop' },
  { label: 'How it works', to: '/how-it-works' },
  { label: 'Labs & testing', to: '/labs' },
  { label: 'Pricing', to: '/pricing' },
  { label: 'About', to: '/about' },
]

export const principles = [
  {
    name: 'Access',
    detail:
      'Care that does not depend on where you live, who your employer is, or whether a specialist has an opening in March.',
  },
  {
    name: 'Transparency',
    detail:
      'The medication, the pharmacy, the dose and the price are published before you enter a payment method.',
  },
  {
    name: 'Choice',
    detail:
      'More than one clinically appropriate option, explained plainly, so the decision stays yours.',
  },
  {
    name: 'Control',
    detail:
      'Your labs, protocol history and documentation are yours to download and take anywhere.',
  },
]

export const trustMarks = [
  { label: 'Licensed clinicians', detail: 'In all 50 states' },
  { label: 'Secure & private', detail: 'HIPAA-compliant records' },
  { label: 'Transparent pricing', detail: 'No hidden dispensing fees' },
  { label: 'Discreet delivery', detail: 'Unbranded, cold-chain when needed' },
]

export const steps = [
  {
    label: 'Intake',
    title: 'Tell us what is actually going on',
    detail:
      'A 10-minute intake covering symptoms, history, medications and goals. No account required to see whether Republic is a fit.',
    duration: '10 minutes',
  },
  {
    label: 'Labs',
    title: 'Get real numbers',
    detail:
      'Collect at home or visit one of 2,100 partner draw sites. Results land in your Republic record, annotated by your clinician.',
    duration: '3–5 days',
  },
  {
    label: 'Protocol',
    title: 'Review the plan before you pay for it',
    detail:
      'Your clinician writes a protocol with the medication, dose, pharmacy and monthly cost stated up front. You approve it.',
    duration: '24–48 hours',
  },
  {
    label: 'Ongoing care',
    title: 'Adjust as the data changes',
    detail:
      'Follow-up panels, dose changes and unlimited messaging are included. Cancel or pause from your account, no phone call.',
    duration: 'Continuous',
  },
]

export const pricingTiers = [
  {
    name: 'Membership',
    price: 39,
    cadence: 'per month',
    description: 'Clinical access and record-keeping, separate from what you pay for medication.',
    features: [
      'Unlimited clinician messaging',
      'Annual baseline panel included',
      'Protocol reviews and dose changes',
      'Downloadable records at any time',
    ],
    accent: false,
  },
  {
    name: 'Membership + Program',
    price: 129,
    cadence: 'per month, from',
    description: 'Everything in Membership, plus the medication for one clinical program.',
    features: [
      'One active program of your choice',
      'Medication from US-licensed pharmacies',
      'Follow-up labs twice per year',
      'Free shipping, cold-chain included',
      'Price locked for 12 months',
    ],
    accent: true,
  },
  {
    name: 'Panels only',
    price: 89,
    cadence: 'one time',
    description: 'For patients who want the data first and will decide on treatment later.',
    features: [
      '62-marker baseline panel',
      'Clinician-annotated results',
      'Written interpretation call available',
      'No membership required',
    ],
    accent: false,
  },
]

export const labPanels = [
  {
    name: 'Baseline',
    markers: 62,
    detail: 'Hormones, metabolic, thyroid, CBC, CMP and lipids — the panel every program starts from.',
  },
  {
    name: 'Extended longevity',
    markers: 104,
    detail: 'Adds ApoB, Lp(a), hs-CRP, homocysteine, IGF-1, insulin and micronutrients.',
  },
  {
    name: 'Follow-up',
    markers: 28,
    detail: 'The subset that tells your clinician whether the current protocol is working.',
  },
]

export const faqs: Faq[] = [
  {
    question: 'Who writes my protocol?',
    answer:
      'A clinician licensed in your state. Republic does not use a fixed formulary or a checkout that ships medication without review — every protocol is written and signed by the clinician who read your labs.',
  },
  {
    question: 'Where do the medications come from?',
    answer:
      'US-licensed 503A and 503B compounding pharmacies and standard retail wholesalers. The pharmacy that filled your order is named on your protocol and on the shipping documentation.',
  },
  {
    question: 'What does transparent pricing actually mean?',
    answer:
      'The membership fee and the medication cost are listed separately. You see the full monthly figure before you approve a protocol, and your price is held for twelve months.',
  },
  {
    question: 'Does Republic take insurance?',
    answer:
      'Republic is a direct-pay practice, which is what keeps pricing predictable. Lab work and some medications are eligible for HSA and FSA reimbursement, and we provide itemized documentation.',
  },
  {
    question: 'Can I keep my own doctor?',
    answer:
      'Yes, and we encourage it. Your labs, protocol and clinical notes are downloadable as a single PDF you can hand to any provider.',
  },
  {
    question: 'What if I want to stop?',
    answer:
      'Pause or cancel from your account without a phone call. Your clinician will provide a taper plan where one is clinically appropriate.',
  },
]

export const testimonials = [
  {
    quote:
      'I had the labs, I had the symptoms, and I still spent two years being told everything was normal. Republic was the first place that read the whole panel back to me.',
    name: 'Marisa T.',
    detail: 'Hormone Therapy for Women · 14 months',
  },
  {
    quote:
      'The protocol arrived with the pharmacy, the lot number and the monthly cost on one page. That is all I ever wanted from a clinic.',
    name: 'Daniel R.',
    detail: 'Hormone Optimization for Men · 9 months',
  },
]
